import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { ITaskInfo } from '../../../interfaces/task-info';
import {
  IFacetedInboxTask,
  IFacetedJob,
  IFacetedSupplier,
} from '../../../interfaces/faceted-inbox-task';
import crossfilter from 'crossfilter2/crossfilter';
import { LazyLoadEvent, SelectItem, Table } from 'primeng';
import { get, orderBy } from 'lodash';
import { OrganisationsService } from '../../../services';
import { OrganisationTypeEnum } from '../../../enums/organisation-types';
import moment from 'moment';

@Component({
  selector: 'vme-interactive-schedule',
  templateUrl: './vme-interactive-schedule.component.html',
  styleUrls: ['./vme-interactive-schedule.component.scss'],
})
export class VMEInteractiveScheduleComponent implements OnInit, AfterViewChecked {
  resetting = false;
  minDate: string;
  maxDate: string;
  isFacetOpen: {} = {};

  constructor(private organisationService: OrganisationsService) {
    this.minDate = new Date().toLocaleDateString();
    this.maxDate = new Date(new Date().setDate(new Date().getDate() + 10)).toLocaleDateString();
  }

  selectedSuppliers: any[];
  regions: any[];
  suppliers: any[] = [];
  supplierIds: number[] = [];
  _inputJobs: IFacetedJob[];
  private changeDetectorRef: any;

  @Input('inputJobs')
  public set inputJobs(value: any) {
    this._inputJobs = value;
  }

  public get inputJobs() {
    return this._inputJobs;
  }

  loadingSelection: boolean = false;

  // the entire resultset
  allData: IFacetedSupplier[];

  // the crossfiltered resultset
  crossFilterData: any;

  // each group creates a selection table in the UI
  filterGroups: any[] = [];

  // the dimensions of the crossfiltered data
  filterDimensions: any[];

  // the selected filter items in each list
  selectedFilters: any[] = [];

  inputTasks: ITaskInfo[] = [];

  // this is all data filtered by the currently selected items within dimensions
  // crossfilter uses this to create dimensions which will then reflect the selected items
  workingSet: IFacetedInboxTask[] = [];
  resultSet: IFacetedInboxTask[]; // the rows shown in the paginated grid

  // sort on value descending or key ascending
  sortType: boolean[] = [];

  // selected job in the results grid
  //selectedJob: IFacetedInboxTask;

  // paginated grid properties
  rowsInPage: number = 99;
  firstRow: number = 0;
  sortField: string;
  sortOrder: number = 0;

  isLoading: boolean = true;
  showLogging: boolean = true;
  refreshTableSearchFilter: boolean = false;
  loadedFromQuery: boolean = false;

  ngOnInit() {
    this.load();
  }

  filterDefinitions: any[] = [
    {
      propertyName: 'contractTypeName',
      title: 'Contract Type',
    },
    {
      propertyName: 'classificationName',
      title: 'Classification',
    },
    {
      propertyName: 'serviceDeliveryGroupName',
      title: 'DeliveryGroup',
    },
    {
      propertyName: 'workingHours',
      title: 'Hours',
    },
    {
      propertyName: 'tags',
      title: 'Tags',
    },
  ];

  @ViewChildren('facetTable') facetTables: QueryList<Table>;

  settingFilters: boolean = false;

  ngAfterViewChecked() {
    if (this.refreshTableSearchFilter) {
      for (let index = 0; index < this.filterDefinitions.length; index++) {
        if (this.filterDefinitions[index].tableFilter) {
          let tf = this.filterDefinitions[index].tableFilter;
          let facetTable = this.facetTables.toArray()[index];

          facetTable.filter(tf.value, tf.field, tf.method);
        }
      }

      this.refreshTableSearchFilter = false;
      this.populateWorkingSet();
    }
  }

  clearDimensions() {
    for (let i = 0; i < this.filterDimensions.length; i++) {
      if (!this.selectedFilters[i]) this.filterDimensions[i].filterAll();
    }
  }

  filterRowSelectionChanged(index: number) {
    this.loadingSelection = true;

    this.clearDimensions();
    this.setDimensionFilter(index);
    this.populateWorkingSet();

    // this.supplierIds = this.workingSet.map(x => x.id );

    // IMPORTANT: Suppliers should be the last filter in the list
    this.loadingSelection = false;
  }

  setDimensionFilter(index: number) {
    //flatten the values in the selection array
    const selectedFilter = get(this.selectedFilters, `[${index}]`);
    const selectedFilterValues =
      selectedFilter && typeof selectedFilter.map === 'function'
        ? selectedFilter.map((x) => {
            return x.value;
          })
        : [];

    if (selectedFilterValues && selectedFilterValues.length > 0) {
      let definition = this.filterDefinitions[index];
      if (!definition) return;

      if (!definition.rangeDivisor && !definition.dateDiff && !definition.ranges) {
        if (!definition.isDate) {
          this.filterDimensions[index].filterFunction((dv) => {
            return selectedFilterValues.indexOf(dv) >= 0;
          });
        } else {
          this.filterDimensions[index].filterFunction((x) => {
            return selectedFilterValues.indexOf(x.toISOString()) >= 0;
          });
        }
      } else if (definition.rangeDivisor) {
        this.filterDimensions[index].filterFunction((d) => {
          selectedFilterValues.forEach((value) => {
            let min = value - (definition.rangeDivisor - 1);
            let max = value + 1;

            const result = d >= min && d <= max;
            if (result) return result;
          });

          return false;
        });
      } else if (definition.dateDiff) {
        this.filterDimensions[index].filterFunction((x) => {
          let today = moment().startOf('day');
          let xDate = moment(x).startOf('day');
          let days = xDate.diff(today, 'days');

          return selectedFilterValues.indexOf(days) >= 0;
        });
      } else if (definition.ranges) {
        this.filterDimensions[index].filterFunction((x) => {
          let result: boolean = false;

          // get min and max range from value 1
          for (let i = 0; i < definition.ranges.length; i++) {
            let range = definition.ranges[i];

            result =
              x >= range.min &&
              x < range.max &&
              selectedFilterValues.indexOf(range.groupValue) >= 0;

            if (result) break;
          }

          return result;
        });
      }
    } else if (this.filterDimensions[index]) {
      this.filterDimensions[index].filterAll();
    }

    this.refreshTableSearchFilter = true;
  }

  populateWorkingSet(saveSelection: boolean = true) {
    // reset groups according to filtered dimensions
    this.setSelections();

    // repopulate the results grid using the selected dimension filters
    this.workingSet = this.crossFilterData.allFiltered();

    this.firstRow = 0;
    this.setResults();

    this.settingFilters = false;

    if (saveSelection) this.saveToStorage();
  }

  async load() {
    this.allData = await this.organisationService.getCrossFilter({
      filters: {
        organisationTypeId: { value: OrganisationTypeEnum.Supplier },
        activeOnly: { value: true },
        taskPickupEarliestDateTime: { value: this.minDate },
        taskDropoffLatestDateTime: { value: this.maxDate },
      },
    });

    // setup all the groups for the current tab
    this.crossFilterData = crossfilter(this.allData);
    this.workingSet = this.crossFilterData.allFiltered();

    //    this.filterGroups = [];
    this.filterDimensions = [];

    this.isLoading = false;
    this.setDimensions();
    this.setResults();
    this.loadFromStorage();
  }

  onGridLazyLoad(event: LazyLoadEvent) {
    this.firstRow = event.first;
    this.rowsInPage = event.rows;
    this.sortField = event.sortField;
    this.sortOrder = event.sortOrder;

    // get data from workingset according to row values
    if (this.workingSet && this.workingSet.length > 0) this.setResults();
  }

  setResults() {
    this.resultSet = this.workingSet
      .sort((a, b) => {
        switch (this.sortField) {
          case 'icon':
            return this.numberSort(
              a.openIncidentCount + a.openMessageCount,
              b.openIncidentCount + b.openMessageCount,
            );
          case 'scheduledDate':
            return this.dateSort(a.scheduledDate, b.scheduledDate);
          case 'jobNumber':
            return this.stringSort(a.jobNumber, b.jobNumber);
          case 'vehicleReg':
            return this.stringSort(a.vehicleReg, b.vehicleReg);
          case 'fromCity':
            return this.stringSort(a.fromCity, b.fromCity);
          case 'toCity':
            return this.stringSort(a.toCity, b.toCity);
          case 'from':
            return this.stringSort(a.from, b.from);
          case 'to':
            return this.stringSort(a.to, b.to);
          case 'pickupZoneName':
            return this.stringSort(a.pickupZoneName, b.pickupZoneName);
          case 'deliveryZoneName':
            return this.stringSort(a.deliveryZoneName, b.deliveryZoneName);
          case 'earliestPickupDate':
            return this.dateSort(a.earliestPickupDate, b.earliestPickupDate);
          case 'earliestDropOffDate':
            return this.dateSort(a.earliestDropOffDate, b.earliestDropOffDate);
          case 'customerTaskType':
            return this.stringSort(a.customerTaskType, b.customerTaskType);
          case 'customerName':
            return this.stringSort(a.customerName, b.customerName);
          case 'supplierName':
            return this.stringSort(a.supplierName, b.supplierName);
          case 'supplierPrice':
            return this.numberSort(a.supplierPrice, b.supplierPrice);
          case 'jobStatus':
            return this.stringSort(a.jobStatus, b.jobStatus);
          case 'inboxTabName':
            return this.stringSort(a.inboxTabName, b.inboxTabName);
          default:
            return this.dateSort(a.scheduledDate, b.scheduledDate);
        }
      })
      .slice(this.firstRow, this.firstRow + this.rowsInPage);

    // clear all filters if no results (can happen with data changes)
    if (this.resultSet.length == 0) {
      this.clearAllFilters();
    }
  }

  stringSort(a, b): number {
    let str1 = !a ? '' : a.toString();
    let str2 = !b ? '' : b.toString();

    if (this.sortOrder == 1) return str1.localeCompare(str2);
    else return str2.localeCompare(str1);
  }

  dateSort(a, b): number {
    if (this.sortOrder == 1) return moment(a).unix() - moment(b).unix();
    else return moment(b).unix() - moment(a).unix();
  }

  numberSort(a, b): number {
    return this.sortOrder == 1 ? a - b : b - a;
  }

  setDimensions() {
    let nullData = 'Not set';
    let nullDataNumber = 0;

    for (let i = 0; i < this.filterDefinitions.length; i++) {
      let def = this.filterDefinitions[i];

      this.filterDimensions[i] = this.crossFilterData.dimension((d) => {
        return (
          (def.isDate ? moment(d[def.propertyName]).startOf('day') : d[def.propertyName]) ||
          (def.ranges ? nullDataNumber : nullData)
        );
      });
    }

    this.setSelections();
  }

  setSelections() {
    for (let i = 0; i < this.filterDefinitions.length; i++) {
      let definition = this.filterDefinitions[i];
      let data: any;
      if (!definition.rangeDivisor && !definition.dateDiff && !definition.ranges) {
        data = this.filterDimensions[i].group().all();
      } else if (definition.rangeDivisor) {
        data = this.filterDimensions[i]
          .group((x) => {
            let val = Math.ceil((x || 0) / definition.rangeDivisor) * definition.rangeDivisor;
            return val || 1;
          })
          .all();
      } else if (definition.dateDiff) {
        data = this.filterDimensions[i]
          .group((x) => {
            let today = moment().startOf('day');
            let xDate = moment(x).startOf('day');
            let days = xDate.diff(today, 'days');

            return days;
          })
          .all();
      } else if (definition.ranges) {
        data = this.filterDimensions[i]
          .group((x) => {
            let val = x || 1;
            let groupVal: number = undefined;

            for (let k = 0; k < definition.ranges.length; k++) {
              let range = definition.ranges[k];

              if (val >= range.min && val < range.max) {
                groupVal = range.groupValue;
                break;
              }
            }

            if (!groupVal) {
              return val;
            } else {
              return groupVal;
            }
          })
          .all();
      }

      let dataArray = data.filter(
        (x) =>
          x.value > 0 ||
          (this.selectedFilters[i]
            ? this.selectedFilters[i]
                .map((x) => {
                  return x.value;
                })
                .indexOf(x.key) >= 0
            : false),
      );

      this.filterGroups[i] = {
        name: definition.title,
        data: dataArray,
        noRangeCheck: definition.noRangeCheck,
        selectItems: dataArray.map((x) => {
          if (!definition.rangeDivisor && !definition.ranges) {
            let lbl: string = x.key;

            if (definition.isDate) {
              lbl = moment(x.key).startOf('day').format('ddd DD MMM YYYY');
            } else if (definition.dateDiff) {
              lbl = `${x.key} days`;
            }

            return {
              label: lbl,
              count: x.value,
              value: definition.isDate ? moment(x.key).startOf('day').toISOString() : x.key,
              id: x.id,
            } as SelectItem;
          } else if (definition.rangeDivisor) {
            let numVal = parseFloat(x.key);

            // calculate label by value
            if (numVal <= definition.rangeDivisor) {
              return {
                label: `Up to ${definition.rangeDivisor}${definition.descriptor}`,
                count: x.value,
                value: x.key,
              } as SelectItem;
            } else {
              let min = numVal;
              let max = numVal + definition.rangeDivisor;
              let lbl = `${min}-${max}${definition.descriptor}`;

              return {
                label: lbl,
                count: x.value,
                value: x.key,
              } as SelectItem;
            }
          } else if (definition.ranges) {
            for (let j = 0; j < definition.ranges.length; j++) {
              if (definition.ranges[j].groupValue == x.key) {
                return {
                  label: definition.ranges[j].label,
                  count: x.value,
                  value: x.key,
                } as SelectItem;
              }
            }

            return {
              label: x.key,
              count: x.value,
              value: x.key,
            } as SelectItem;
          }
        }),
      };

      if (!this.filterGroups[i].noRangeCheck) {
        this.filterGroups[i].selectItems.forEach((x) => {
          x.dataOutOfRange = this.dataOutOfRange(x);
        });
      }

      this.resetSelectedItems(i);

      // add the selected items at top of list then the unselected
      this.orderSelectedUnselectedItems(i);
    }
  }

  orderSelectedUnselectedItems(index: number) {
    let reordered = [];
    let selectedFilters = this.filterGroups[index].selectItems.filter((x) =>
      this.selectedFilters[index] ? this.selectedFilters[index].indexOf(x) >= 0 : false,
    );
    let unselectedFilters = this.filterGroups[index].selectItems.filter((x) =>
      this.selectedFilters[index] ? this.selectedFilters[index].indexOf(x) == -1 : false,
    );

    // Default whether facet is open by default
    if (!(index in this.isFacetOpen)) {
      this.isFacetOpen[index] = selectedFilters.length > 0;
    }

    reordered.push(...selectedFilters, ...unselectedFilters);

    this.filterGroups[index].selectItems = reordered;
  }

  applyFilter(tableIndex: number, value: string, field: string, method): void {
    let facetTable = this.facetTables.toArray()[tableIndex];

    facetTable.filter(value, field, method);

    // store the filter in the definitions array so it can be reapplied when filters are refreshed
    this.filterDefinitions[tableIndex].tableFilter = { value, field, method };
  }

  handleFilter(event, total) {}

  resetSelectedItems(i: number) {
    // reset selected items to reflect current filter
    let resetItems = [];

    for (let j = 0; j < this.filterGroups[i].selectItems.length; j++) {
      let item = this.filterGroups[i].selectItems[j];

      if (this.selectedFilters[i] && typeof this.selectedFilters[i].forEach === 'function') {
        this.selectedFilters[i].forEach((x) => {
          if (x.value == item.value) {
            resetItems.push(item);
          }
        });
      }
    }

    this.selectedFilters[i] = resetItems;

    if (!(i in this.isFacetOpen)) {
      this.isFacetOpen[i] = resetItems.length > 0;
    }
  }

  loadFromStorage() {
    if (this.loadedFromQuery) {
      return;
    }

    let stored = JSON.parse(localStorage.getItem('schedule-suppliers'));
    if (stored) {
      this.selectedFilters = stored;
    }

    this.isFacetOpen = {};
    this.settingFilters = true;
    this.setFiltersFromSelection();
    this.settingFilters = false; // Reset before leaving callback method
  }

  setFiltersFromSelection() {
    setTimeout(() => {
      // Call setTimeout to run code asynchronously
      for (let i = 0; i < this.selectedFilters.length; i++) {
        this.setDimensionFilter(i);
        this.resetSelectedItems(i);
      }
      this.populateWorkingSet(false);
    }, 10);
  }

  saveToStorage() {
    localStorage.setItem('schedule-suppliers', JSON.stringify(this.selectedFilters));
  }

  vehicleCountPostfix(item, value) {
    return this.itemsCountPostfix(item, value, item.vehiclesNum);
  }

  itemsCountPostfix(item, value, count) {
    if (value == null || value == '' || count < 2) return value;
    return value + ' (+' + (count - 1) + ')';
  }

  sortFilter(index: number) {
    this.sortType[index] = !this.sortType[index];
    let filterGroup = this.filterGroups[index];

    if (this.sortType[index]) {
      filterGroup.selectItems = orderBy(
        filterGroup.selectItems,
        (o) => {
          return o.count;
        },
        'desc',
      );
    } else {
      filterGroup.selectItems = orderBy(
        filterGroup.selectItems,
        (o) => {
          return o.value;
        },
        'asc',
      );
    }

    // set selecteditem positions to top of array
    this.orderSelectedUnselectedItems(index);

    this.resetSelectedItems(index);
  }

  // if selections are specified they will be set after clear
  showSearch: boolean = true;
  supplierSearchKeyword: any = '';

  clearAllFilters(selections = null) {
    this.settingFilters = true;
    this.selectedSuppliers = [];
    this.supplierIds = [];
    this.selectedFilters.fill(null);
    this.clearDimensions();

    setTimeout(() => {
      // Call setTimeout to run code asynchronously

      this.populateWorkingSet();

      if (selections) {
        this.selectedFilters = selections;
        this.setFiltersFromSelection();
      }
    }, 10);
  }

  dataOutOfRange(data): boolean {
    let format = 'YYYY-MM-DDTHH:mm:ss.SSS[Z]';
    let date = moment(data.value, format, true);
    let isDate = date.isValid();

    if (isDate) {
      return date < moment().subtract(1, 'd');
    } else if (this.isNumeric(data.value)) {
      return data.value < 0;
    }

    return false;
  }

  isNumeric(num) {
    num = '' + num; //coerce num to be a string
    let result = !isNaN(num) && !isNaN(parseFloat(num));
    return result;
  }

  getSelectedFiltersString(i: number) {
    return this.selectedFilters[i]
      .map(
        (x) =>
          '<div style="margin-top: 0px; margin-bottom: 4px; padding-top: 0px; padding-bottom: 4px; border-bottom: 1px solid #666;">' +
          x.label +
          '</div>',
      )
      .join('');
  }

  applySupplierFilter(value: any) {}

  changeSelectedSuppliers($event) {
    this.supplierIds = this.selectedSuppliers.map((x) => x.id);
  }

  workingSetWithoutSelected() {
    return this.workingSet.filter(
      (x) =>
        !this.supplierIds.includes(x.id) &&
        (this.supplierSearchKeyword == '' ||
          x.supplierName.toLowerCase().includes(this.supplierSearchKeyword.toLowerCase())),
    );
  }

  async dateChange(event: { minDate: string; maxDate: string }) {
    this.minDate = event.minDate;
    this.maxDate = event.maxDate;

    await this.load();

    // Refresh values in selected Suppliers
    let previouslySelected = this.selectedSuppliers ? this.selectedSuppliers.map((x) => x.id) : [];
    this.selectedSuppliers = this.workingSet.filter((x) => previouslySelected.includes(x.id));
  }
}
